import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useDeleteProposalGroup from '@/api/mutations/proposals/useDeleteProposalGroup';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import {
  DeleteProposalGroupInput,
  DeleteProposalGroupMutation,
} from '@/gql/graphql';

import { applyProposalGroupDeletion } from './applyProposalGroupDeletion';
import { createOptimisticProposalMutationOptions } from './createOptimisticProposalMutation';

/**
 * Optimistic wrapper around {@link useDeleteProposalGroup}: removes the group
 * and its descendants from the cached proposal in `onMutate` so it disappears
 * immediately, then rolls back on error. The base hook invalidates the
 * proposal on success, reconciling with server truth.
 */
const useOptimisticDeleteProposalGroup = (args: {
  proposalId: string;
  options?: UseMutationOptions<
    DeleteProposalGroupMutation,
    unknown,
    DeleteProposalGroupInput
  >;
}) => {
  const { proposalId, options } = args;
  const queryClient = useQueryClient();
  const queryKey = proposalKeys.proposal(proposalId);

  return useDeleteProposalGroup({
    proposalId,
    options: {
      ...options,
      ...createOptimisticProposalMutationOptions({
        queryClient,
        queryKey,
        apply: applyProposalGroupDeletion,
        options,
      }),
    },
  });
};

export default useOptimisticDeleteProposalGroup;
